import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Plus, Search, MessageSquare, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '@/lib/utils';
import { llmApi, LlmConversationListItem, LlmPreset } from '@/api/llm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Только что';
  if (diffMins < 60) return `${diffMins} мин. назад`;
  if (diffHours < 24) return `${diffHours} ч. назад`;
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return `${diffDays} дн. назад`;
  return formatDate(dateString);
}

export default function LlmPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [showPresetSheet, setShowPresetSheet] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Fetch conversations
  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['llm-conversations', search],
    queryFn: () => llmApi.getConversations({ search: search || undefined, limit: 50 }),
  });

  // Fetch presets for new conversation
  const { data: presets } = useQuery({
    queryKey: ['llm-presets'],
    queryFn: () => llmApi.getPresets(),
  });

  // Create conversation mutation
  const createConversation = useMutation({
    mutationFn: (presetId?: string) => llmApi.createConversation({ presetId }),
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: ['llm-conversations'] });
      navigate(`/more/chat/${conversation.id}`);
    },
    onError: (error: any) => {
      toast.error('Ошибка', {
        description: error.response?.data?.message || 'Не удалось создать беседу',
      });
    },
  });

  const deleteConversation = useMutation({
    mutationFn: (id: string) => llmApi.deleteConversation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['llm-conversations'] });
      queryClient.removeQueries({ queryKey: ['llm-conversation', id] });
      toast.success('Чат удалён');
    },
    onError: () => {
      toast.error('Не удалось удалить чат');
    },
    onSettled: () => {
      setDeleteTargetId(null);
    },
  });

  const handleNewConversation = () => {
    // If only one preset or no presets, create directly
    if (!presets || presets.length <= 1) {
      createConversation.mutate(presets?.[0]?.id);
    } else {
      setShowPresetSheet(true);
    }
  };

  const handleSelectPreset = (preset: LlmPreset) => {
    setShowPresetSheet(false);
    createConversation.mutate(preset.id);
  };

  const conversations = conversationsData?.items || [];

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <header className="flex items-center gap-2 px-4 py-3 border-b bg-card">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/more')}
          className="shrink-0"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold flex-1">AI Чат</h1>
        <Button
          size="sm"
          onClick={handleNewConversation}
          disabled={createConversation.isPending}
        >
          {createConversation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          <span className="ml-1">Новый</span>
        </Button>
      </header>

      {/* Search */}
      <div className="p-3 border-b bg-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по беседам..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
            <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
            <p>{search ? 'Ничего не найдено' : 'Нет бесед'}</p>
            {!search && (
              <Button
                variant="link"
                onClick={handleNewConversation}
                disabled={createConversation.isPending}
              >
                Создать первую беседу
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                onClick={() => navigate(`/more/chat/${conversation.id}`)}
                onDelete={() => setDeleteTargetId(conversation.id)}
                isDeleting={
                  deleteConversation.isPending &&
                  deleteConversation.variables === conversation.id
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Preset Selection Sheet */}
      <Sheet open={showPresetSheet} onOpenChange={setShowPresetSheet}>
        <SheetContent side="bottom" className="h-[60vh]">
          <SheetHeader>
            <SheetTitle>Выберите модель</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {presets?.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleSelectPreset(preset)}
                className={cn(
                  'w-full p-4 rounded-lg border text-left transition-colors',
                  'hover:bg-accent focus:bg-accent focus:outline-hidden',
                  preset.isDefault && 'border-primary'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{preset.name}</span>
                  {preset.isDefault && (
                    <span className="text-xs text-primary">По умолчанию</span>
                  )}
                </div>
                {preset.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {preset.description}
                  </p>
                )}
                <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                  <span>{preset.model}</span>
                  <span>•</span>
                  <span>{preset.serviceTier}</span>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => {
          if (!open && !deleteConversation.isPending) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить чат?</AlertDialogTitle>
            <AlertDialogDescription>
              Чат будет удалён без возможности восстановления.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteConversation.isPending}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteConversation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTargetId) deleteConversation.mutate(deleteTargetId);
              }}
            >
              {deleteConversation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Удалить'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConversationItem({
  conversation,
  onClick,
  onDelete,
  isDeleting,
}: {
  conversation: LlmConversationListItem;
  onClick: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="relative flex items-stretch hover:bg-accent focus-within:bg-accent transition-colors">
      <button
        onClick={onClick}
        className="flex-1 min-w-0 p-4 pr-12 text-left focus:outline-hidden"
      >
        <div className="flex items-baseline gap-2">
          <h3 className="font-medium truncate flex-1 min-w-0">
            {conversation.title || 'Новая беседа'}
          </h3>
          <span className="text-xs text-muted-foreground shrink-0">
            {formatRelativeDate(conversation.updatedAt)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <span>{conversation.preset?.name || conversation.model}</span>
          <span>•</span>
          <span>{conversation._count.messages} сообщ.</span>
        </div>
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        aria-label="Удалить чат"
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md',
          'text-muted-foreground hover:text-destructive active:text-destructive',
          'focus:outline-hidden focus:ring-2 focus:ring-destructive/40',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        )}
      >
        {isDeleting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Trash2 className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}
